import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { SIGNUP_URL, FLYER_URL, BUTTON_URL, IG_URL, STORY_URL, TRACK_URL, CAPTION, HOTSPOTS } from "./flyerConstants";

import { API } from "./api";

export default function FlyerPage() {
  const [copied, setCopied] = useState(false);
  const [taps, setTaps] = useState(null);
  const [leaders, setLeaders] = useState(null);

  const refreshTaps = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/track/total`);
      setTaps(data.total);
    } catch {
      setTaps(null);
    }
  }, []);

  const refreshLeaders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/referrals/leaderboard`);
      setLeaders(data.leaders);
    } catch {
      setLeaders(null);
    }
  }, []);

  useEffect(() => {
    refreshTaps();
    refreshLeaders();
  }, [refreshTaps, refreshLeaders]);

  const trackedClick = () => {
    setTimeout(refreshTaps, 3000);
    setTimeout(refreshLeaders, 3000);
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(CAPTION);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = CAPTION;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      data-testid="flyer-viewer"
      className="flex flex-col items-center px-4 py-10"
    >
      <div className="relative w-full max-w-md">
        <img
          data-testid="flyer-image"
          src={FLYER_URL}
          alt="HOPE CDS Business Credit Lines funding flyer"
          className="w-full rounded-xl shadow-2xl shadow-blue-900/60"
        />
        {HOTSPOTS.map((h) => (
          <a
            key={h.id}
            data-testid={h.id}
            href={TRACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={h.label}
            onClick={trackedClick}
            className="absolute block rounded-lg ring-[#ffcd3c] hover:ring-4 hover:bg-[#ffcd3c]/10 transition"
            style={h.style}
          />
        ))}
      </div>

      <a
        data-testid="signup-button-link"
        href={TRACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackedClick}
        className="mt-8 block w-full max-w-sm hover:scale-[1.03] active:scale-[0.99] transition-transform"
      >
        <img
          data-testid="signup-button-image"
          src={BUTTON_URL}
          alt="Sign Up Today — opens the funding signup page"
          className="w-full drop-shadow-[0_10px_24px_rgba(255,205,60,0.35)]"
        />
      </a>
      <p className="mt-5 text-sm text-blue-200/70 text-center" data-testid="flyer-hint">
        Tap the gold button — it opens the funding signup page directly.
      </p>

      <div data-testid="click-counter" className="mt-4 text-sm font-bold text-[#ffcd3c] text-center">
        {taps === null
          ? ""
          : taps === 0
            ? "Be the first — tap the signup link!"
            : `${taps} ${taps === 1 ? "person has" : "people have"} tapped the signup link`}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-4 w-full max-w-md">
        <a
          data-testid="download-flyer-btn"
          href={FLYER_URL}
          download="HOPE-CDS-flyer-signup-link.png"
          className="flex-1 text-center rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 px-6 hover:brightness-110 transition"
        >
          Download Flyer
        </a>
        <a
          data-testid="download-button-btn"
          href={BUTTON_URL}
          download="SIGN-UP-TODAY-button.png"
          className="flex-1 text-center rounded-full border-2 border-[#ffcd3c] text-[#ffcd3c] font-bold py-3 px-6 hover:bg-[#ffcd3c]/10 transition"
        >
          Download Button
        </a>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-4 w-full max-w-md">
        <a
          data-testid="download-ig-btn"
          href={IG_URL}
          download="HOPE-CDS-flyer-1080x1350.jpg"
          className="flex-1 text-center rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 px-6 hover:brightness-110 transition"
        >
          Download IG Post (4:5)
        </a>
        <a
          data-testid="download-story-btn"
          href={STORY_URL}
          download="HOPE-CDS-flyer-1080x1920.jpg"
          className="flex-1 text-center rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 px-6 hover:brightness-110 transition"
        >
          Download Story (9:16)
        </a>
      </div>

      <div
        data-testid="caption-card"
        className="mt-8 w-full max-w-md bg-[#0a1c46] border border-white/10 rounded-2xl p-6"
      >
        <h2 data-testid="caption-title" className="text-[#ffcd3c] font-bold text-lg">
          Post Caption — ready to paste
        </h2>
        <p
          data-testid="caption-text"
          className="mt-4 text-sm text-blue-100/90 whitespace-pre-wrap leading-relaxed"
        >
          {CAPTION}
        </p>
        <button
          data-testid="copy-caption-btn"
          onClick={copyCaption}
          className="mt-5 w-full rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 hover:brightness-110 transition"
        >
          {copied ? "Copied!" : "Copy Caption"}
        </button>
      </div>

      <div
        data-testid="leaderboard-card"
        className="mt-8 w-full max-w-md bg-[#0a1c46] border border-white/10 rounded-2xl p-6"
      >
        <h2 data-testid="leaderboard-title" className="text-[#ffcd3c] font-bold text-lg">
          Community leaderboard
        </h2>
        <p className="text-xs text-blue-200/50 mt-1">Top members driving signups this week</p>
        {leaders === null ? (
          <p className="mt-4 text-sm text-blue-200/70">Loading…</p>
        ) : leaders.length === 0 ? (
          <p data-testid="leaderboard-empty" className="mt-4 text-sm text-blue-200/70">
            No signups this week yet — share your link and claim the top spot!
          </p>
        ) : (
          <ol data-testid="leaderboard-list" className="mt-4 space-y-2">
            {leaders.map((l, i) => (
              <li
                key={l.display + i}
                data-testid="leaderboard-entry"
                className="flex items-center justify-between rounded-lg bg-[#04102e] border border-white/10 px-4 py-2.5"
              >
                <span className="text-sm text-blue-100/90">
                  <span className="text-[#ffcd3c] font-bold mr-2">#{i + 1}</span>
                  {l.display}
                </span>
                <span className="text-sm font-bold text-[#ffcd3c]">
                  {l.count} {l.count === 1 ? "signup" : "signups"}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
